import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Edit,
  Eye,
  Building,
  Phone,
  Mail,
  CreditCard
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Switch } from '../../components/ui/switch';
import { usersApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const Customers = () => {
  const { isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [editModal, setEditModal] = useState(null);
  const [viewCustomer, setViewCustomer] = useState(null);
  
  const [editData, setEditData] = useState({
    name: '',
    company_name: '',
    phone: '',
    role: '',
    credit_limit: '',
    is_active: true
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await usersApi.getAll();
      setCustomers(response.data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      await usersApi.update(editModal.id, {
        name: editData.name,
        company_name: editData.company_name || null,
        phone: editData.phone || null,
        role: editData.role,
        credit_limit: editData.credit_limit ? parseFloat(editData.credit_limit) : 0,
        is_active: editData.is_active
      });
      toast.success('User updated successfully');
      setEditModal(null);
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const openEditModal = (customer) => {
    setEditModal(customer);
    setEditData({
      name: customer.name,
      company_name: customer.company_name || '',
      phone: customer.phone || '',
      role: customer.role,
      credit_limit: customer.credit_limit?.toString() || '0',
      is_active: customer.is_active !== false
    });
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      admin: 'bg-purple-50 text-purple-700 border-purple-200',
      sales: 'bg-blue-50 text-blue-700 border-blue-200',
      customer: 'bg-green-50 text-green-700 border-green-200',
      warehouse: 'bg-amber-50 text-amber-700 border-amber-200',
    };
    return classes[role] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.company_name && customer.company_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesRole = roleFilter === 'all' || customer.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="space-y-6" data-testid="customers-loading">
        <div className="skeleton h-12 w-48 rounded-md"></div>
        <div className="skeleton h-96 rounded-md"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Users & Customers</h1>
          <p className="text-slate-600 mt-1">
            {customers.filter(c => c.role === 'customer').length} customers, {customers.length} total users
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="customer-search"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="warehouse">Warehouse</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <CardContent className="p-0">
          {filteredCustomers.length === 0 ? (
            <div className="empty-state py-16">
              <Users className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No users found</h3>
              <p className="text-slate-500">Try adjusting your search filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Credit Limit</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.map((customer) => (
                    <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-xs text-slate-500">{customer.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{customer.company_name || '-'}</TableCell>
                      <TableCell>
                        <Badge className={`${getRoleBadgeClass(customer.role)} capitalize border`}>
                          {customer.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {customer.role === 'customer' ? (
                          `$${(customer.credit_limit || 0).toLocaleString()}`
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {customer.is_active !== false ? (
                          <Badge className="bg-green-50 text-green-700 border border-green-200">Active</Badge>
                        ) : (
                          <Badge className="bg-red-50 text-red-700 border border-red-200">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8"
                            onClick={() => setViewCustomer(customer)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-8 w-8"
                              onClick={() => openEditModal(customer)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Modal */}
      <Dialog open={!!editModal} onOpenChange={() => setEditModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="form-label">Name</Label>
              <Input
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                data-testid="edit-name-input"
              />
            </div>

            <div>
              <Label className="form-label">Company Name</Label>
              <Input
                value={editData.company_name}
                onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
              />
            </div>

            <div>
              <Label className="form-label">Phone</Label>
              <Input
                value={editData.phone}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
              />
            </div>

            <div>
              <Label className="form-label">Role</Label>
              <Select 
                value={editData.role} 
                onValueChange={(value) => setEditData({ ...editData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="warehouse">Warehouse</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {editData.role === 'customer' && (
              <div>
                <Label className="form-label">Credit Limit ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="100"
                  value={editData.credit_limit}
                  onChange={(e) => setEditData({ ...editData, credit_limit: e.target.value })}
                  data-testid="credit-limit-input"
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label>Account Active</Label>
              <Switch
                checked={editData.is_active}
                onCheckedChange={(checked) => setEditData({ ...editData, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditModal(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} className="bg-orange-600 hover:bg-orange-700" data-testid="save-user-button">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Customer Modal */}
      <Dialog open={!!viewCustomer} onOpenChange={() => setViewCustomer(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {viewCustomer && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-slate-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{viewCustomer.name}</h3>
                  <Badge className={`${getRoleBadgeClass(viewCustomer.role)} capitalize border`}>
                    {viewCustomer.role}
                  </Badge>
                </div>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3 text-slate-600">
                  <Mail className="w-4 h-4" />
                  {viewCustomer.email}
                </div>
                {viewCustomer.phone && (
                  <div className="flex items-center gap-3 text-slate-600">
                    <Phone className="w-4 h-4" />
                    {viewCustomer.phone}
                  </div>
                )}
                {viewCustomer.company_name && (
                  <div className="flex items-center gap-3 text-slate-600">
                    <Building className="w-4 h-4" />
                    {viewCustomer.company_name}
                  </div>
                )}
                {viewCustomer.role === 'customer' && (
                  <div className="flex items-center gap-3 text-slate-600">
                    <CreditCard className="w-4 h-4" />
                    Credit Limit: ${(viewCustomer.credit_limit || 0).toLocaleString()}
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-slate-200 text-sm text-slate-500">
                Joined: {viewCustomer.created_at ? new Date(viewCustomer.created_at).toLocaleDateString() : 'Unknown'}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
